import React, { useEffect, useState } from 'react';
import { Box, Button, Text } from 'grommet';
import { NavLink, useNavigate } from 'react-router-dom';
import ChildGrid, {
  MoreColumn,
  StatusIcon
} from '../../../components/child-grid';
import Loader from '../../../components/loader';
import * as itemTypes from '../../../items/item-types';
import {
  authorizedFetch,
  dateFormatOptions,
  generateNewId,
  isItemNew,
  getPlacementLocationNames
} from '../../../utils';
import EmbeddedFlowPreviewModal from './embedded-flow-preview-modal';
import PartnerRevShareModal from './partner-rev-share/partner-rev-share-modal';
import { canUpdate } from '../../../auth';
import { fetchAdUnitTemplates } from '../traffic-partner-utils';

export const IMPLEMENTATION_TYPES_LABELS = {
  pageoverlay: 'Page Overlay',
  embedonpage: 'Embed on Page',
  stackoverlay: 'Stack Overlay',
  stackembed: 'Stack Embed',
  adUnitConfig: 'adunitconfig'
};

const buildConditionsString = requestingUrlContainsFilters => {
  let conditionsString;
  const urlConditions = requestingUrlContainsFilters.map(f => `"${f}"`);
  if (urlConditions) {
    conditionsString = `${urlConditions.join(' OR ')}`;
  }
  return conditionsString;
};

const getColumns = (
  onEdit,
  onStatusChange,
  setDefault,
  setPreviewEmbeddedFlow,
  placementLocationNames
) => [
  {
    property: 'embeddedFlowConfig.name',
    header: 'Name',
    width: '200px',
    render: embeddedFlowConfig => {
      return (
        <Text truncate title={embeddedFlowConfig.name}>
          {embeddedFlowConfig.name}
        </Text>
      );
    },
    sort: embeddedFlowConfig => {
      return embeddedFlowConfig.name;
    },
    defaultOrder: 'asc'
  },
  {
    property: 'embeddedFlowConfig.sourceName',
    header: 'Source Name | ID',
    width: '240px',
    render: embeddedFlowConfig => {
      return (
        <Box direction='row' align='center' gap='small'>
          <Text truncate title={embeddedFlowConfig.trafficSourceDisplayName}>
            <NavLink
              to={`/${itemTypes.TRAFFIC_SOURCE}/${embeddedFlowConfig.trafficSourceId}/info`}
            >
              {embeddedFlowConfig.trafficSourceDisplayName}{' '}
            </NavLink>
          </Text>
        </Box>
      );
    },
    sort: embeddedFlowConfig => {
      return embeddedFlowConfig.trafficSourceDisplayName;
    },
    defaultOrder: 'asc'
  },
  {
    property: 'embeddedFlowConfig.placementLocationId',
    header: 'Placement Location',
    width: '200px',
    render: embeddedFlowConfig => {
      const placementLocationName =
        embeddedFlowConfig.placementExperienceId &&
        placementLocationNames[embeddedFlowConfig.placementLocationId]
          ? placementLocationNames[embeddedFlowConfig.placementLocationId]
          : '';
      return <Text>{placementLocationName || ''}</Text>;
    },
    sort: embeddedFlowConfig => {
      return `${embeddedFlowConfig.placementLocationName} | ${embeddedFlowConfig.placementLocationId}`;
    },
    defaultOrder: 'asc'
  },
  {
    property: 'embeddedFlowConfig.templateDisplayName',
    header: 'Template',
    width: '260px',

    render: embeddedFlowConfig => {
      return (
        <Text truncate title={embeddedFlowConfig.templateDisplayName}>
          {embeddedFlowConfig.templateDisplayName}
        </Text>
      );
    },
    sort: embeddedFlowConfig => {
      return embeddedFlowConfig.templateDisplayName;
    },
    defaultOrder: 'asc'
  },
  {
    property: 'embeddedFlowConfig.urlCondition',
    header: 'URL Condition Contains',
    render: embeddedFlowConfig => {
      const requestingUrlContainsFilters =
        embeddedFlowConfig.requestingUrlContainsFilters || [];
      const testRequestingUrlContainsFilters =
        embeddedFlowConfig.testRequestingUrlContainsFilters || [];
      const combinedFilters = [
        ...requestingUrlContainsFilters,
        ...testRequestingUrlContainsFilters
      ];
      const conditionsString = buildConditionsString(combinedFilters);
      return (
        <Box direction='row' align='center' gap='small'>
          <Text title={conditionsString ?? ''} truncate>
            {conditionsString ?? ''}
          </Text>
        </Box>
      );
    },
    sort: embeddedFlowConfig => {
      const requestingUrlContainsFilters =
        embeddedFlowConfig.requestingUrlContainsFilters || [];
      const testRequestingUrlContainsFilters =
        embeddedFlowConfig.testRequestingUrlContainsFilters || [];
      const combinedFilters = [
        ...requestingUrlContainsFilters,
        ...testRequestingUrlContainsFilters
      ];
      const conditionsString = buildConditionsString(combinedFilters);
      return conditionsString ?? '';
    },
    defaultOrder: 'asc'
  },
  {
    property: 'embeddedFlowConfig.enabled',
    header: 'Status',
    width: '100px',
    render: embeddedFlowConfig => {
      return (
        <StatusIcon
          status={embeddedFlowConfig.enabled ? 'Active' : 'Inactive'}
          size='small'
        />
      );
    },
    sort: 'enabled',
    defaultOrder: 'desc'
  },
  {
    property: 'embeddedFlowConfig.dateModified',
    header: 'Modified On (By)',
    width: '230px',
    sort: embeddedFlowConfig =>
      `${
        embeddedFlowConfig.dateModified
      } (${embeddedFlowConfig.lastModifiedBy || 'None'})`,
    defaultOrder: 'desc',
    render: embeddedFlowConfig => {
      const dateModifiedAndModifiedBy = `${embeddedFlowConfig.dateModified &&
        new Date(embeddedFlowConfig.dateModified).toLocaleString(
          'en-US',
          dateFormatOptions
        )} (${embeddedFlowConfig.lastModifiedBy || 'None'})`;
      return (
        <Text truncate title={dateModifiedAndModifiedBy}>
          {dateModifiedAndModifiedBy}
        </Text>
      );
    }
  },
  {
    property: 'action',
    header: 'Actions',
    width: '70px',
    render: embeddedFlowConfig => {
      const isLegacyConfig =
        !!embeddedFlowConfig.flowId && !embeddedFlowConfig.adUnitTemplateId;
      const extraOptions = isLegacyConfig
        ? []
        : [
            {
              label: 'Preview',
              action: () => setPreviewEmbeddedFlow(embeddedFlowConfig)
            }
          ];

      return (
        <MoreColumn
          onEdit={onEdit}
          status={embeddedFlowConfig.enabled ? 'Active' : 'Inactive'}
          onStatusChange={onStatusChange}
          canChangeStatus={
            (!isLegacyConfig || embeddedFlowConfig.enabled) &&
            canUpdate(itemTypes.EMBEDDED_FLOW_CONFIG)
          }
          entity={embeddedFlowConfig}
          extraOptions={extraOptions}
        />
      );
    }
  }
];

export const getEmbeddedFlowConfigs = async (
  id,
  setEmbeddedFlowConfigs,
  setIsLoadingItems,
  addError
) => {
  if (!isItemNew(id)) {
    setIsLoadingItems(true);
    try {
      const response = await authorizedFetch(
        `/api/${itemTypes.TRAFFIC_PARTNER}/${id}/${itemTypes.EMBEDDED_FLOW_CONFIG}/search`,
        'POST',
        {
          query: '',
          from: 0,
          size: 1000,
          orderByField: 'dateModified',
          orderByAsc: false
        }
      );
      const embeddedFlowConfigs = response?.items ?? [];
      setEmbeddedFlowConfigs(embeddedFlowConfigs);
      setIsLoadingItems(false);
    } catch (error) {
      addError('Unable to fetch ad unit configurations', JSON.stringify(error));
      setIsLoadingItems(false);
    }
  }
};

const EmbeddedFlowConfigGrid = props => {
  const navigate = useNavigate();
  const { item, addError, addSuccess, modifyItem, canEdit } = props;
  const [embeddedFlowConfigs, setEmbeddedFlowConfigs] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(true);
  const [gridData, setGridData] = useState([]);
  const [showManagePartnerRevenue, setShowManagePartnerRevenue] = useState(
    false
  );
  const [previewEmbeddedFlow, setPreviewEmbeddedFlow] = useState();
  const [placementLocationNames, setPlacementLocationNames] = useState({});
  const [adUnitTemplates, setAdUnitTemplates] = useState([]);
  const [customAdUnitTemplates, setCustomAdUnitTemplates] = useState([]);

  useEffect(() => {
    getEmbeddedFlowConfigs(
      item.id,
      setEmbeddedFlowConfigs,
      setIsLoadingItems,
      addError
    );
  }, [item.id, addError]);

  useEffect(() => {
    fetchAdUnitTemplates(
      setAdUnitTemplates,
      setCustomAdUnitTemplates,
      addError
    );
  }, [addError]);

  useEffect(() => {
    if (embeddedFlowConfigs.length > 0) {
      const placementLocationIds = embeddedFlowConfigs
        .map(config => config.placementLocationId)
        .filter(id => id && id.length > 0);

      if (placementLocationIds.length > 0) {
        getPlacementLocationNames(
          placementLocationIds,
          setPlacementLocationNames,
          addError
        );
      }
    }
  }, [embeddedFlowConfigs, addError]);

  useEffect(() => {
    const getGridData = async () => {
      const gridData = embeddedFlowConfigs.map(efc => {
        return {
          ...efc
        };
      });
      setGridData(gridData);
    };
    getGridData();
  }, [embeddedFlowConfigs]);

  useEffect(() => {
    setGridData(gridData);
  }, [gridData]);

  const createEmbeddedFlowConfig = () =>
    navigate(
      `/${itemTypes.TRAFFIC_PARTNER}/${item.id}/${
        itemTypes.EMBEDDED_FLOW_CONFIG
      }/${generateNewId()}/info`
    );

  const openEmbeddedFlowConfig = embeddedFlowConfig =>
    navigate(
      `/${itemTypes.TRAFFIC_PARTNER}/${item.id}/${itemTypes.EMBEDDED_FLOW_CONFIG}/${embeddedFlowConfig.id}/info`
    );

  const updateConfigStatus = async (updatedConfig, newStatus) => {
    try {
      authorizedFetch(
        `/api/${itemTypes.TRAFFIC_PARTNER}/${item.id}/${
          itemTypes.EMBEDDED_FLOW_CONFIG
        }/${updatedConfig.id}/${
          newStatus === 'Active' ? 'activate' : 'deactivate'
        }`,
        'POST',
        {
          expectedVersion: updatedConfig.version
        }
      ).then(res => {
        const newEmbeddedFlowConfigs = [...embeddedFlowConfigs];
        const updatedConfigIndex = newEmbeddedFlowConfigs.findIndex(
          config => config.id === updatedConfig.id
        );
        newEmbeddedFlowConfigs[updatedConfigIndex].enabled =
          newStatus === 'Active' ? true : false;
        newEmbeddedFlowConfigs[updatedConfigIndex].version = res.version;
        setEmbeddedFlowConfigs(newEmbeddedFlowConfigs);
      });
    } catch (ex) {
      addError('Unable to change config status', ex.message);
    }
  };

  const setDefaultConfig = embeddedFlowConfig => {
    modifyItem({ defaultEmbeddedFlowConfigId: embeddedFlowConfig.id });
  };

  return (
    <Box flex>
      <Box
        direction='column'
        justify='between'
        align='right'
        flex={{ shrink: 0 }}
      >
        {!isLoadingItems ? (
          <Box
            style={{
              position: 'relative'
            }}
          >
            {canUpdate(itemTypes.EMBEDDED_FLOW_CONFIG) && (
              <Button
                label='Manage Partner Revenue'
                onClick={() => setShowManagePartnerRevenue(true)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '190px'
                }}
                disabled={gridData.length === 0}
              />
            )}
            <ChildGrid
              header='Ad Unit Configurations'
              items={gridData}
              itemType={itemTypes.EMBEDDED_FLOW_CONFIG}
              keyBuilder={embeddedFlowConfig => embeddedFlowConfig.id}
              columns={getColumns(
                openEmbeddedFlowConfig,
                updateConfigStatus,
                setDefaultConfig,
                setPreviewEmbeddedFlow,
                placementLocationNames
              )}
              newLabel='New Configuration'
              noLabel='Add new configurations using the button above.'
              onItemDoubleClick={openEmbeddedFlowConfig}
              onNewClick={createEmbeddedFlowConfig}
            />

            {showManagePartnerRevenue && (
              <PartnerRevShareModal
                setShowPlaceholdersModal={setShowManagePartnerRevenue}
                gridData={gridData}
                trafficPartnerId={item.id}
                addError={addError}
                canEdit={canEdit}
              />
            )}

            {previewEmbeddedFlow ? (
              <EmbeddedFlowPreviewModal
                trafficPartnerId={item.id}
                previewEmbeddedFlow={previewEmbeddedFlow}
                setPreviewEmbeddedFlow={setPreviewEmbeddedFlow}
                canEdit={canEdit}
                addError={addError}
                addSuccess={addSuccess}
                adUnitTemplates={adUnitTemplates}
                customAdUnitTemplates={customAdUnitTemplates}
              />
            ) : null}
          </Box>
        ) : (
          <Loader />
        )}
      </Box>
    </Box>
  );
};

EmbeddedFlowConfigGrid.displayName = 'EmbeddedFlowConfigGrid';

export default EmbeddedFlowConfigGrid;
